import React, { CSSProperties, ReactNode } from 'react'
import classNames from 'classnames'
import { baseSkeleton, block, blockWidth, filler, skBorderRadius, square, squareSize } from './Skeleton.css'
import { assignInlineVars } from '@vanilla-extract/dynamic'

interface SquareSkeletonProps {
	sideLength?: number
	cornerRadius?: number
	children?: ReactNode
}
interface BlockSkeletonProps {
	length?: number
	cornerRadius?: number
	children?: ReactNode
	style?: CSSProperties | undefined
}

/**
 * Skeleton for a single line of text
 * @constructor
 */
export const SquareSkeleton = ({ cornerRadius, sideLength = 50 }: SquareSkeletonProps): JSX.Element => {
	return (
		<Square cornerRadius={cornerRadius} sideLength={sideLength}>
			<Filler />
		</Square>
	)
}

const Square = ({ cornerRadius, sideLength = 50, children }: SquareSkeletonProps) => {
	const borderRadius = cornerRadius ? `${cornerRadius}px` : 'inherit'
	return (
		<div
			className={square}
			style={assignInlineVars({ [squareSize]: `${sideLength}px`, [skBorderRadius]: borderRadius })}
		>
			{children}
		</div>
	)
}

const Filler = () => {
	return <span className={classNames(baseSkeleton, filler)}>&zwnj;</span>
}

const Block = ({ cornerRadius, length = 100, style, children }: BlockSkeletonProps) => {
	const borderRadius = cornerRadius ? `${cornerRadius}px` : 'inherit'
	const skStyle = style ?? assignInlineVars({ [blockWidth]: `${length}%`, [skBorderRadius]: borderRadius })
	return (
		<div className={block} style={skStyle}>
			{children}
		</div>
	)
}

interface CircleSkeletonProps {
	rectLength?: number
}

export const CircleSkeleton = ({ rectLength = 50 }: CircleSkeletonProps): JSX.Element => (
	<Square cornerRadius={rectLength / 2} sideLength={rectLength}>
		<Filler />
	</Square>
)

export const GreedySkeleton = (): JSX.Element => <Filler />

export const BlockSkeleton = ({ cornerRadius, length }: BlockSkeletonProps): JSX.Element => (
	<Block cornerRadius={cornerRadius} length={length}>
		<Filler />
	</Block>
)

export const StyledSkeleton: React.FC<React.PropsWithChildren<Partial<React.CSSProperties>>> = props => (
	<Block style={props}>
		<Filler />
	</Block>
)
